import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { infoHints, type HintKey } from '@/lib/infoHints';
import { cn } from '@/lib/utils';

interface Props {
  hintKey: HintKey;
  /** 'inline' = 12px (next to a row label). 'sidebar' = 14px (page headings). */
  variant?: 'inline' | 'sidebar';
  className?: string;
}

/**
 * Hover/focus/tap-triggered tooltip explaining what a number on the UI
 * actually means. Copy comes from a single file so the whole app's
 * glossary lives in one place; see src/lib/infoHints.ts.
 */
export function InfoHint({ hintKey, variant = 'inline', className }: Props) {
  const hint = infoHints[hintKey];
  const size = variant === 'inline' ? 12 : 14;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`What is ${hint.label}?`}
          className={cn(
            'inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <Info size={size} aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="start">
        <div className="font-medium mb-1">{hint.label}</div>
        <div className="text-muted-foreground">{hint.detail}</div>
      </TooltipContent>
    </Tooltip>
  );
}
